import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Database, ArrowLeft, Mail, CheckCircle2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const ForgotPasswordPage = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      toast.error('Mohon masukkan email Anda');
      return;
    }

    setLoading(true);
    
    try {
      await axios.post(`${API}/auth/forgot-password`, { email });
      setEmailSent(true);
      toast.success('Email reset password telah dikirim!');
    } catch (error) {
      console.error('Forgot password error:', error);
      const errorMsg = error.response?.data?.detail || 'Gagal mengirim email reset';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-card rounded-xl shadow-2xl p-8 border border-border">
            {/* Success Icon */}
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" strokeWidth={2} />
              </div>
            </div>

            {/* Success Message */}
            <div className="text-center mb-6">
              <h2 className="text-2xl font-bold mb-2" style={{fontFamily: 'Space Grotesk'}}>
                Email Terkirim!
              </h2>
              <p className="text-muted-foreground text-sm">
                Kami telah mengirim link reset password ke:
              </p>
              <p className="text-primary font-semibold mt-2">{email}</p>
            </div>

            {/* Instructions */}
            <div className="bg-accent/30 rounded-lg p-4 mb-6">
              <p className="text-sm text-muted-foreground">
                📧 Cek inbox email Anda dan klik link yang diberikan untuk reset password.
              </p>
              <p className="text-sm text-muted-foreground mt-2">
                Link berlaku selama <strong>1 jam</strong>.
              </p>
            </div>

            {/* Back to Login */}
            <Link to="/login">
              <Button className="w-full" variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" strokeWidth={1.5} />
                Kembali ke Login
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-xl shadow-2xl p-8 border border-border">
          {/* Logo & Title */}
          <div className="flex items-center justify-center mb-6">
            <div className="bg-primary/10 p-3 rounded-full">
              <Database className="w-8 h-8 text-primary" strokeWidth={1.5} />
            </div>
          </div>
          
          <div className="text-center mb-8">
            <h1 className="text-2xl font-bold mb-2" style={{fontFamily: 'Space Grotesk'}}>
              Lupa Password?
            </h1>
            <p className="text-muted-foreground text-sm">
              Masukkan email Anda dan kami akan kirimkan link untuk reset password
            </p>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Input */}
            <div>
              <label className="block text-sm font-medium mb-2">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="nama@example.com"
                  className="w-full pl-10 pr-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-all"
                  disabled={loading}
                  required
                />
              </div>
            </div>

            {/* Submit Button */}
            <Button 
              type="submit" 
              className="w-full" 
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                  Mengirim...
                </>
              ) : (
                <>
                  <Mail className="w-4 h-4 mr-2" strokeWidth={1.5} />
                  Kirim Link Reset
                </>
              )}
            </Button>
          </form>

          {/* Back to Login */}
          <div className="mt-6 text-center">
            <Link 
              to="/login" 
              className="text-sm text-muted-foreground hover:text-primary transition-colors inline-flex items-center"
            >
              <ArrowLeft className="w-3 h-3 mr-1" strokeWidth={1.5} />
              Kembali ke Login
            </Link>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-6 text-xs text-muted-foreground">
          Tidak menerima email? Cek folder spam atau coba lagi.
        </div>
      </div>
    </div>
  );
};
